/**
 * Reading `production_lots.color_summary` safely.
 *
 * The column is free-form JSON and there are **two** live shapes:
 *
 * 1. Manual lot creation (shopfloor order detail form):
 *      `[{ "color": "Black", "quantity": 120 }]`
 * 2. Kiosk / unified completion (and therefore every basket line):
 *      `[{ "color_name": "Black", "size_label": "39-41", "quantity": 120 }]`
 *
 * Older rows may also be a plain map (`{"Black": 120}`), a nested wrapper
 * (`{"colors": [...]}`) or a JSON string. The previous reader only understood
 * shape 1, so kiosk-created lots rendered as `undefined: 120`.
 */

export interface LotColorEntry {
  color: string
  size: string | null
  quantity: number | null
}

/**
 * Any shape `production_lots.color_summary` may carry. It is free-form JSON:
 * a list of objects (either key naming), a plain map, or a JSON string.
 * Always read it through `parseLotColorSummary` / `formatLotColorSummary`.
 */
export type LotColorSummary = string | Record<string, unknown> | unknown[] | null | undefined

const COLOR_KEYS = ['color', 'color_name', 'colour', 'name', 'label', 'title']
const SIZE_KEYS = ['size', 'size_label', 'size_name']
const QUANTITY_KEYS = ['quantity', 'qty', 'value', 'amount', 'count', 'pairs']
const WRAPPER_KEYS = ['colors', 'colours', 'items', 'rows', 'data', 'lines', 'summary']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(source: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim() !== '') return value.trim()
  }
  return null
}

function readNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value)
    }
  }
  return null
}

/** Accepts an object, an array, a JSON string or anything else. */
function coerce(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const text = value.trim()
  if (text === '') return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    // A bare colour name is a usable last resort ("Black").
    return text
  }
}

/** Unwrap `{ colors: [...] }`-style containers. */
function unwrap(value: unknown): unknown {
  let current = coerce(value)
  for (let depth = 0; depth < 3; depth += 1) {
    if (!isRecord(current)) return current
    // Bind to a const so the narrowing survives inside the callbacks below.
    const record: Record<string, unknown> = current
    const wrapper = WRAPPER_KEYS.map((key) => record[key]).find((candidate) => candidate !== undefined)
    if (wrapper === undefined) return current
    current = coerce(wrapper)
  }
  return current
}

function entryFrom(record: Record<string, unknown>): LotColorEntry | null {
  const color = readString(record, COLOR_KEYS)
  const quantity = readNumber(record, QUANTITY_KEYS)
  if (color === null && quantity === null) return null
  return { color: color ?? '', size: readString(record, SIZE_KEYS), quantity }
}

/**
 * Normalise any known `color_summary` shape into entries.
 * Entries without a colour are kept when they carry a quantity, so a number is
 * never silently dropped; `formatLotColorSummary` decides how to render them.
 */
export function parseLotColorSummary(summary: unknown): LotColorEntry[] {
  const value = unwrap(coerce(summary))

  if (typeof value === 'string') {
    return value === '' ? [] : [{ color: value, size: null, quantity: null }]
  }

  if (Array.isArray(value)) {
    const entries: LotColorEntry[] = []
    for (const item of value) {
      const normalised = coerce(item)
      if (isRecord(normalised)) {
        const entry = entryFrom(normalised)
        if (entry) entries.push(entry)
        continue
      }
      // `["Black", "Navy"]`
      if (typeof normalised === 'string' && normalised.trim() !== '') {
        entries.push({ color: normalised.trim(), size: null, quantity: null })
      }
    }
    return entries
  }

  if (isRecord(value)) {
    const entries: LotColorEntry[] = []
    for (const [key, raw] of Object.entries(value)) {
      const normalised = coerce(raw)
      if (isRecord(normalised)) {
        const entry = entryFrom(normalised)
        // `{"Black": {"quantity": 120}}` — the key already carries the colour.
        if (entry) {
          entries.push({ ...entry, color: entry.color || key })
        } else {
          entries.push({ color: key, size: null, quantity: readNumber(normalised, QUANTITY_KEYS) })
        }
        continue
      }
      const quantity = typeof normalised === 'number' ? normalised : readNumber({ value: normalised }, QUANTITY_KEYS)
      entries.push({ color: key, size: null, quantity })
    }
    return entries
  }

  return []
}

/**
 * `"Black 39-41: 120, Navy: 80"` — never `undefined`, never `[object Object]`.
 * A quantity without a colour still shows as a bare number rather than hiding
 * the row's data; a colour without a quantity shows just the name.
 */
export function formatLotColorSummary(summary: unknown): string {
  return parseLotColorSummary(summary)
    .map((entry) => {
      const label = entry.size ? `${entry.color} ${entry.size}` : entry.color
      if (entry.quantity === null) return label
      return label === '' ? String(entry.quantity) : `${label}: ${entry.quantity}`
    })
    .filter((text) => text !== '')
    .join(', ')
}

/** Distinct colour names only (used by the kiosk's colour chips). */
export function lotColorNames(summary: unknown): string[] {
  const names: string[] = []
  for (const entry of parseLotColorSummary(summary)) {
    if (entry.color !== '' && !names.includes(entry.color)) names.push(entry.color)
  }
  return names
}
